import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "../../i18n/TranslationProvider";
import sallieChatBg from "../../images/HIFIVE.png";

/**
 * Scripted FAQ "chatbot" — a quick, interactive version of the Resources page.
 * No backend. Reuses the existing resources.* translation keys (EN/ES),
 * so it stays in sync with the Resources page text.
 *
 * Visible to everyone (logged in or out, all user types).
 * The "See full Resources page" link only shows for logged-in patients/caregivers.
 *
 * Panel height is adjustable: drag the grip on the top edge (double-click resets).
 * The chosen height is remembered per browser via localStorage.
 */
// Styles live here so the chatbot is a single self-contained file.
// Rendered once via a <style> tag (keeps :hover, dark mode and media queries working).
const CHATBOT_STYLES = `
/* Swap these for the app's brand colors if they differ */
.chatbot-root {
    --chatbot-primary: #3a8a8c;
    --chatbot-primary-dark: #2e6f71;
    --chatbot-bg: #ffffff;
    --chatbot-surface: #f3f1ee;
    --chatbot-text: #222222;
    --chatbot-border: #dddddd;

    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 1050; /* above bootstrap navbars, below bootstrap modals (1055) */
    font-size: 0.95rem;
}

body.dark-mode .chatbot-root {
    --chatbot-bg: #1f1f1f;
    --chatbot-surface: #2b2b2b;
    --chatbot-text: #eeeeee;
    --chatbot-border: #444444;
}

/* Floating launcher bubble */
.chatbot-launcher {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: var(--chatbot-primary);
    color: #fff;
    font-size: 1.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
}

.chatbot-launcher:hover {
    background: var(--chatbot-primary-dark);
}

/* Chat panel (height is set inline from state; this is the fallback) */
.chatbot-panel {
    position: relative;
    width: 360px;
    height: 420px;
    max-height: calc(100vh - 110px);
    margin-bottom: 12px;
    background: var(--chatbot-bg);
    color: var(--chatbot-text);
    border: 1px solid var(--chatbot-border);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Translucent Sallie watermark behind the messages */
.chatbot-panel::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: var(--chatbot-bg-image);
    background-repeat: no-repeat;
    background-position: center 55%;
    background-size: 80% auto;
    opacity: 0.12;
    pointer-events: none;
    z-index: 0;
}

body.dark-mode .chatbot-panel::before {
    opacity: 0.2; /* dark grey dog needs a bit more on a dark background */
}

/* Keep panel content above the watermark */
.chatbot-header,
.chatbot-body,
.chatbot-footer {
    position: relative;
    z-index: 1;
}

/* Drag handle along the top edge */
.chatbot-resize {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 10px;
    cursor: ns-resize;
    touch-action: none;
    z-index: 2;
}

.chatbot-resize::after {
    content: "";
    display: block;
    width: 40px;
    height: 4px;
    margin: 3px auto 0;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.55);
}

.chatbot-resize:hover::after,
.chatbot-root.is-resizing .chatbot-resize::after {
    background: rgba(255, 255, 255, 0.9);
}

/* Stop text getting selected while dragging */
.chatbot-root.is-resizing {
    user-select: none;
}

.chatbot-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 14px 12px; /* a little extra top room for the grip */
    background: var(--chatbot-primary);
    color: #fff;
}

.chatbot-title {
    font-weight: 600;
}

.chatbot-icon-btn {
    background: transparent;
    border: none;
    color: #fff;
    font-size: 1.1rem;
    cursor: pointer;
    margin-left: 6px;
}

.chatbot-body {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Message bubbles */
.chatbot-msg {
    max-width: 85%;
    padding: 8px 12px;
    border-radius: 12px;
    line-height: 1.4;
}

.chatbot-msg p {
    margin: 0 0 6px;
}

.chatbot-msg p:last-child {
    margin-bottom: 0;
}

.chatbot-msg ol,
.chatbot-msg ul {
    padding-left: 18px;
    margin: 0;
}

.chatbot-msg--bot {
    align-self: flex-start;
    background: var(--chatbot-surface);
}

.chatbot-msg--user {
    align-self: flex-end;
    background: var(--chatbot-primary);
    color: #fff;
}

/* Resource screenshots are sized for the full page; make them fit the bubble */
.chatbot-msg img {
    width: 100% !important;
    height: auto !important;
    margin: 6px 0;
    border-radius: 6px;
}

.chatbot-video-list {
    list-style: none;
    padding-left: 0 !important;
}

.chatbot-video-list li {
    margin-bottom: 8px;
}

/* Quick-reply option buttons */
.chatbot-options {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 4px;
}

.chatbot-option {
    text-align: left;
    background: var(--chatbot-bg);
    color: var(--chatbot-primary);
    border: 1px solid var(--chatbot-primary);
    border-radius: 18px;
    padding: 6px 12px;
    cursor: pointer;
    text-decoration: none;
    display: block;
}

.chatbot-option:hover {
    background: var(--chatbot-primary);
    color: #fff;
    text-decoration: none;
}

.chatbot-option--secondary {
    border-color: var(--chatbot-border);
    color: var(--chatbot-text);
}

/* Persistent help ticket footer */
.chatbot-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 12px;
    border-top: 1px solid var(--chatbot-border);
    background: var(--chatbot-surface);
    font-size: 0.85rem;
}

.chatbot-help-btn {
    background: var(--chatbot-primary);
    color: #fff;
    border-radius: 18px;
    padding: 6px 12px;
    text-decoration: none;
    white-space: nowrap;
}

.chatbot-help-btn:hover {
    background: var(--chatbot-primary-dark);
    color: #fff;
    text-decoration: none;
}

/* Mobile: full-screen panel, no resizing */
@media (max-width: 576px) {
    .chatbot-root {
        right: 12px;
        bottom: 12px;
    }

    .chatbot-panel {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100% !important; /* override the inline drag height */
        max-height: none;
        margin: 0;
        border-radius: 0;
    }

    .chatbot-resize {
        display: none;
    }

    /* Panel header already has a close button; hide the bubble so it doesn't cover the footer */
    .chatbot-launcher[aria-expanded="true"] {
        display: none;
    }
}
`;

// Panel height limits (px). Max also respects the CSS max-height of calc(100vh - 110px).
const DEFAULT_HEIGHT = 420;
const MIN_HEIGHT = 280;
const HEIGHT_STORAGE_KEY = "chatbot.panelHeight";

const maxPanelHeight = () => Math.max(MIN_HEIGHT, window.innerHeight - 110);
const clampHeight = (h) => Math.min(maxPanelHeight(), Math.max(MIN_HEIGHT, h));

const loadSavedHeight = () => {
    try {
        const saved = parseInt(localStorage.getItem(HEIGHT_STORAGE_KEY), 10);
        return Number.isFinite(saved) ? saved : DEFAULT_HEIGHT;
    } catch {
        return DEFAULT_HEIGHT;
    }
};

const saveHeight = (h) => {
    try {
        localStorage.setItem(HEIGHT_STORAGE_KEY, String(Math.round(h)));
    } catch {
        // storage blocked (private mode etc.) — height just won't persist
    }
};

// Only these user types get the "See full Resources page" link
const RESOURCES_LINK_USER_TYPES = ["patient", "caregiver"];

// Each answer is a list of translation keys rendered as paragraphs (text only, no images).
const SECTIONS = [
    {
        key: "accountHelp",
        faqs: [
            { title: "resources.accountHelp.faq1.title", answer: ["resources.accountHelp.faq1.content"] },
            { title: "resources.accountHelp.faq2.title", answer: ["resources.accountHelp.faq2.content"] },
            { title: "resources.accountHelp.faq3.title", answer: ["resources.accountHelp.faq3.content"] },
        ],
    },
    {
        key: "providerAssignment",
        faqs: [
            { title: "resources.providerAssignment.faq1.title", answer: ["resources.providerAssignment.faq1.content"] },
            { title: "resources.providerAssignment.faq2.title", answer: ["resources.providerAssignment.faq2.content"] },
        ],
    },
    {
        key: "dashboard",
        faqs: [
            { title: "resources.dashboard.faq1.title", answer: ["resources.dashboard.faq1.content"] },
            {
                title: "resources.dashboard.faq2.title",
                answer: [
                    "resources.dashboard.faq2.intro",
                    "resources.dashboard.faq2.item1",
                    "resources.dashboard.faq2.item2",
                ],
            },
            { title: "resources.dashboard.faq3.title", answer: ["resources.dashboard.faq3.content"] },
            { title: "resources.dashboard.faq4.title", answer: ["resources.dashboard.faq4.content"] },
        ],
    },
    {
        key: "exercise",
        faqs: [1, 2, 3, 4, 5].map((n) => ({
            title: `resources.exercise.faq${n}.title`,
            answer: [`resources.exercise.faq${n}.content`],
        })),
    },
    {
        key: "messaging",
        faqs: [
            {
                title: "resources.messaging.faq1.title",
                answer: ["resources.messaging.faq1.step1", "resources.messaging.faq1.step2"],
            },
            {
                title: "resources.messaging.faq2.title",
                answer: ["resources.messaging.faq2.step1", "resources.messaging.faq2.step2"],
            },
            { title: "resources.messaging.faq3.title", answer: ["resources.messaging.faq3.content"] },
        ],
    },
];

const Chatbot = ({ user }) => {
    const { t } = useTranslation();
    const location = useLocation();

    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [step, setStep] = useState("categories"); // "categories" | "questions" | "answered"
    const [activeSection, setActiveSection] = useState(null);
    const bodyRef = useRef(null);

    // Resizable panel height
    const [panelHeight, setPanelHeight] = useState(() => clampHeight(loadSavedHeight()));
    const [isResizing, setIsResizing] = useState(false);
    const dragStartY = useRef(0);
    const dragStartHeight = useRef(0);

    const greeting = () => ({ from: "bot", text: [t("chatbot.greeting")] });

    useEffect(() => {
        if (isOpen && messages.length === 0) setMessages([greeting()]);
    }, [isOpen]);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [messages, step]);

    // Close when navigating (e.g. after clicking "See full Resources page")
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    // Keep the panel within bounds if the window gets smaller
    useEffect(() => {
        const onWindowResize = () => setPanelHeight((h) => clampHeight(h));
        window.addEventListener("resize", onWindowResize);
        return () => window.removeEventListener("resize", onWindowResize);
    }, []);

    // Drag the top edge: moving up makes the panel taller (it's anchored to the bottom)
    const onResizeStart = (e) => {
        e.preventDefault();
        dragStartY.current = e.clientY;
        dragStartHeight.current = panelHeight;
        setIsResizing(true);
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onResizeMove = (e) => {
        if (!isResizing) return;
        const delta = dragStartY.current - e.clientY;
        setPanelHeight(clampHeight(dragStartHeight.current + delta));
    };

    const onResizeEnd = (e) => {
        if (!isResizing) return;
        setIsResizing(false);
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        saveHeight(panelHeight);
    };

    const resetHeight = () => {
        const h = clampHeight(DEFAULT_HEIGHT);
        setPanelHeight(h);
        saveHeight(h);
    };

    const canSeeResources = !!user && RESOURCES_LINK_USER_TYPES.includes(user.userType);

    const push = (...msgs) => setMessages((prev) => [...prev, ...msgs]);

    const selectSection = (section) => {
        setActiveSection(section);
        push(
            { from: "user", text: [t(`resources.${section.key}.title`)] },
            { from: "bot", text: [t("chatbot.pickQuestion")] }
        );
        setStep("questions");
    };

    const selectQuestion = (faq) => {
        push(
            { from: "user", text: [t(faq.title)] },
            { from: "bot", text: faq.answer.map((key) => t(key)) }
        );
        setStep("answered");
    };

    const backToTopics = () => {
        setActiveSection(null);
        push({ from: "bot", text: [t("chatbot.pickTopic")] });
        setStep("categories");
    };

    const restart = () => {
        setActiveSection(null);
        setMessages([greeting()]);
        setStep("categories");
    };

    const renderOptions = () => {
        if (step === "categories") {
            return SECTIONS.map((section) => (
                <button key={section.key} className="chatbot-option" onClick={() => selectSection(section)}>
                    {t(`resources.${section.key}.title`)}
                </button>
            ));
        }

        if (step === "questions" && activeSection) {
            return (
                <>
                    {activeSection.faqs.map((faq) => (
                        <button key={faq.title} className="chatbot-option" onClick={() => selectQuestion(faq)}>
                            {t(faq.title)}
                        </button>
                    ))}
                    <button className="chatbot-option chatbot-option--secondary" onClick={backToTopics}>
                        ← {t("chatbot.backToTopics")}
                    </button>
                </>
            );
        }

        // answered
        return (
            <>
                {/* Only logged-in patients/caregivers: /resources requires login, and these FAQs are patient/caregiver content.
                    The help ticket button is always available in the panel footer. */}
                {canSeeResources && (
                    <Link className="chatbot-option" to="/resources">
                        {t("chatbot.seeResources")}
                    </Link>
                )}
                <button className="chatbot-option" onClick={() => setStep("questions")}>
                    {t("chatbot.moreQuestions")}
                </button>
                <button className="chatbot-option chatbot-option--secondary" onClick={backToTopics}>
                    ← {t("chatbot.backToTopics")}
                </button>
            </>
        );
    };

    return (
        <div className={`chatbot-root${isResizing ? " is-resizing" : ""}`}>
            <style>{CHATBOT_STYLES}</style>
            {isOpen && (
                <div
                    className="chatbot-panel"
                    role="dialog"
                    aria-label={t("chatbot.title")}
                    style={{ height: panelHeight, "--chatbot-bg-image": `url(${sallieChatBg})` }}
                >
                    <div
                        className="chatbot-resize"
                        onPointerDown={onResizeStart}
                        onPointerMove={onResizeMove}
                        onPointerUp={onResizeEnd}
                        onPointerCancel={onResizeEnd}
                        onDoubleClick={resetHeight}
                        title="Drag to resize · double-click to reset"
                        aria-hidden="true"
                    />

                    <div className="chatbot-header">
                        <span className="chatbot-title">{t("chatbot.title")}</span>
                        <div>
                            <button className="chatbot-icon-btn" onClick={restart} aria-label={t("chatbot.restart")} title={t("chatbot.restart")}>
                                ↺
                            </button>
                            <button className="chatbot-icon-btn" onClick={() => setIsOpen(false)} aria-label={t("chatbot.close")} title={t("chatbot.close")}>
                                ✕
                            </button>
                        </div>
                    </div>

                    <div className="chatbot-body" ref={bodyRef} aria-live="polite">
                        {messages.map((msg, i) => (
                            <div key={i} className={`chatbot-msg chatbot-msg--${msg.from}`}>
                                {msg.text.map((line, j) => (
                                    <p key={j}>{line}</p>
                                ))}
                            </div>
                        ))}
                        <div className="chatbot-options">{renderOptions()}</div>
                    </div>

                    {/* Always-visible help ticket option */}
                    <div className="chatbot-footer">
                        <span>{t("resources.help.title")}</span>
                        <Link className="chatbot-help-btn" to="/help">
                            {t("resources.help.button")}
                        </Link>
                    </div>
                </div>
            )}

            <button
                className="chatbot-launcher"
                onClick={() => setIsOpen((open) => !open)}
                aria-label={isOpen ? t("chatbot.close") : t("chatbot.open")}
                aria-expanded={isOpen}
            >
                {isOpen ? "✕" : "💬"}
            </button>
        </div>
    );
};

export default Chatbot;